import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { log } from '@/lib/logger';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/form/textarea';
import { Send, Brain, User, Loader2, Mic } from 'lucide-react';
import { cn } from '@/lib/utils';
import { sendCoachChat, type PlanItem, type CoachChatResponse } from '@/lib/api';
import { generateCoachGreeting } from '@/lib/coachGreeting';
import { CoachProgressPanel } from './CoachProgressPanel';
import { PlanList } from './PlanList';
import { WeeklyPlanPreviewCard } from './WeeklyPlanPreviewCard';
import { CoachProgressList } from './CoachProgressList';
import { RunnerProcessingIndicator } from './RunnerProcessingIndicator';
import { SystemProcessingIndicator } from './SystemProcessingIndicator';
import { TacticalMacros } from './TacticalMacros';
import { TypewriterText } from './TypewriterText';
import { CoachMarkdown } from './CoachMarkdown';
import { usePlanningProgressStore } from '@/stores/planningProgressStore';
import { PlanningProgressPanel } from '@/components/planning/PlanningProgressPanel';
import { LoadPlanToCalendarButton, hasLoadablePlan } from './LoadPlanToCalendarButton';
import { useQueryClient } from '@tanstack/react-query';
import { fetchConversationMessages, type ConversationMessage } from '@/lib/api/coach';
import { classifyApiError } from '@/lib/api/errorClassification';
import { VoiceButton } from '@/components/voice/VoiceButton';
import { VoiceOverlay } from '@/components/voice/VoiceOverlay';
import type { VoiceTranscript } from '@/hooks/useVoiceSession';

type CoachMode = 'idle' | 'awaiting_intent' | 'planning' | 'executing' | 'done';

interface Message {
  id: string;
  type: 'user' | 'assistant' | 'progress' | 'final';
  role: 'coach' | 'athlete';
  content: string;
  timestamp: Date;
  progress_stage?: string;
  stage?: string; // Backend stage field (STRUCTURE, WEEKS, WEEK_DETAIL, INSTRUCTIONS, DONE)
  show_plan?: boolean;
  plan_items?: PlanItem[];
  response_type?: 'plan' | 'weekly_plan' | 'season_plan' | 'session_plan' | 'recommendation' | 'summary' | 'greeting' | 'question' | 'explanation' | 'smalltalk' | 'plan_applied' | 'plan_not_applied';
  transient?: boolean;
  message_type?: 'assistant' | 'progress' | 'final';
  metadata?: {
    week_number?: number | string;
    total_weeks?: number | string;
    [key: string]: unknown;
  };
  /** True while a voice assistant message is still streaming deltas. */
  isVoiceStreaming?: boolean;
  /** Identifies this message as originating from voice conversation. */
  source?: 'voice' | 'text';
  /** Full coach response for plan messages — used to extract calendar sessions for "Load into Calendar". */
  coachResponse?: CoachChatResponse;
}

/**
 * Detects if the last user message has plan intent
 */
const hasPlanIntent = (message: string): boolean => {
  const lower = message.toLowerCase();
  // Exclude read-only / analytics queries that mention training keywords
  if (/\b(show|display|analyze|review|check|what|how|tell me|give me|feedback|last run|last ride|last workout|my load|training load|load curve|ctl|atl|tsb)\b/.test(lower)) {
    return false;
  }
  // Match explicit plan-creation patterns
  return (
    /\b(plan|build|create|generate|schedule|design)\s+(my|a|the|me|this)\b/.test(lower) ||
    /\b(plan)\s+(this\s+)?(week|season|race|day|next)\b/.test(lower) ||
    /\b(build|create|generate)\s+(me\s+)?(a\s+)?(week|season|race|plan|workout|schedule)\b/.test(lower)
  );
};

const PREVIEW_MESSAGE_PREFIX = 'Preview: Week of';

/** Placeholder reply from backend when execution starts; we do not show it - only show Apply to calendar when plan is ready. */
const EXECUTING_PLACEHOLDER_REPLY = "I'm working on your request. You'll see updates as I progress.";

/** Plan preview: do NOT render as chat. Show WeeklyPlanPreviewCard only. */
function isPlanPreviewMessage(message: Message): boolean {
  if (message.role !== 'coach') return false;
  const content = message.content.trimStart();
  if (content.startsWith(PREVIEW_MESSAGE_PREFIX)) return true;
  if (
    content.includes('prepared your training week') &&
    content.includes('Apply this plan')
  ) {
    return true;
  }
  return false;
}

/** Keep only the last plan-preview message when multiple exist (avoids triple repeat from polling/streaming). */
function deduplicatePlanPreviewMessages(msgs: Message[]): Message[] {
  const previewIndices = msgs
    .map((m, i) => (isPlanPreviewMessage(m) ? i : -1))
    .filter((i) => i >= 0);
  if (previewIndices.length <= 1) return msgs;
  const lastIndex = previewIndices[previewIndices.length - 1];
  return msgs.filter((_, i) => !isPlanPreviewMessage(msgs[i]) || i === lastIndex);
}

/** True if content looks like raw API/confirm JSON (do not show to user). */
function looksLikeRawJson(content: string): boolean {
  const s = content.trim();
  return (
    s.startsWith('{') &&
    (s.includes('"revision_id"') || s.includes('"applied"') || s.includes('"response_type"'))
  );
}

/**
 * User-facing message for apply/confirm responses. Never show raw JSON.
 * - plan_applied: "Ready, sessions applied to calendar" (or backend message if it's plain text).
 * - plan_not_applied: friendly error (or backend message if it's plain text).
 */
function formatApplyConfirmReply(
  reply: string | undefined,
  responseType: string | undefined,
  appliedMsg: string,
  notAppliedMsg: string
): string {
  const raw = (reply ?? '').trim();
  const isJson = looksLikeRawJson(raw);
  if (responseType === 'plan_applied') {
    if (!raw || isJson) return appliedMsg;
    return raw;
  }
  if (responseType === 'plan_not_applied') {
    if (!raw || isJson) return notAppliedMsg;
    return raw;
  }
  return raw || '';
}

interface CoachNavigationState {
  context?: 'modify_today_session' | 'widget_redirect';
  session_id?: string | null;
  suggested_action?: 'generic' | 'skip' | 'reduce_volume' | 'convert_to_recovery';
  draft_message?: string;
}

export function CoachChat() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const applyConfirmReply = (reply: string | undefined, responseType: string | undefined) =>
    formatApplyConfirmReply(reply, responseType, t('coach.coachChat.planApplied'), t('coach.coachChat.planNotApplied'));
  const [mode, setMode] = useState<CoachMode>('idle');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [hasEditedDraft, setHasEditedDraft] = useState(false);
  const [draftContext, setDraftContext] = useState<CoachNavigationState | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isSendingRef = useRef<boolean>(false);
  const finalPlanRef = useRef<Message | null>(null);
  /** Set when backend indicates plan was applied (confirm success). Used to invalidate calendar only once. */
  const planAppliedRef = useRef<boolean>(false);
  const resetProgress = usePlanningProgressStore((state) => state.reset);
  const queryClient = useQueryClient();
  const [progressStages, setProgressStages] = useState<Array<{
    id: string;
    label: string;
    status: 'completed' | 'in_progress' | 'pending';
  }>>([]);
  const [proposedSteps, setProposedSteps] = useState<Array<{ id: string; label: string }>>([]);

  // ---- Voice → Chat sync ----
  /** Maps transcript array index → chat message id for deduplication. */
  const voiceSyncMapRef = useRef<Map<number, string>>(new Map());

  /** Sync voice transcripts into the chat message list (display-only, no API calls). */
  const handleTranscriptUpdate = useCallback((transcripts: VoiceTranscript[]) => {
    const syncMap = voiceSyncMapRef.current;

    setMessages((prev) => {
      const updated = [...prev];

      for (let i = 0; i < transcripts.length; i++) {
        const t = transcripts[i];
        if (!t.text.trim()) continue;

        const existingMsgId = syncMap.get(i);

        if (existingMsgId) {
          // Update existing message (streaming text grows, or final replaces streaming)
          const idx = updated.findIndex((m) => m.id === existingMsgId);
          if (idx >= 0) {
            updated[idx] = {
              ...updated[idx],
              content: t.text,
              isVoiceStreaming: !t.final && t.role === 'assistant',
            };
          }
        } else {
          // New transcript entry — create a Message
          const msgId = `voice-${Date.now()}-${i}`;
          syncMap.set(i, msgId);

          updated.push({
            id: msgId,
            type: t.role === 'assistant' ? 'assistant' : 'user',
            role: t.role === 'assistant' ? 'coach' : 'athlete',
            content: t.text,
            timestamp: new Date(t.ts),
            isVoiceStreaming: !t.final && t.role === 'assistant',
            source: 'voice',
          });
        }
      }

      return updated;
    });
  }, []);

  /** Clean up voice state and finalize any streaming messages when overlay closes. */
  const handleVoiceClose = useCallback(() => {
    setMessages((prev) =>
      prev.map((m) => (m.isVoiceStreaming ? { ...m, isVoiceStreaming: false } : m)),
    );
    voiceSyncMapRef.current = new Map();
    setVoiceOpen(false);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle navigation state with draft message (e.g. modify session or session feedback)
  useEffect(() => {
    const state = location.state as CoachNavigationState | null;
    if (!state?.draft_message) return;
    setDraftContext(state.context === 'modify_today_session' ? state : null);
    setInput(state.draft_message);
    setHasEditedDraft(false);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, navigate, location.pathname]);

  // Set initial greeting on mount - only once when idle and no messages
  useEffect(() => {
    if (messages.length === 0) {
      const greeting = generateCoachGreeting(null);
      setMessages([
        {
          id: '1',
          type: 'assistant',
          role: 'coach',
          content: greeting,
          timestamp: new Date(),
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  /**
   * Converts backend ConversationMessage to frontend Message format.
   * For plan_applied/plan_not_applied, never show raw JSON — use friendly copy.
   */
  const convertBackendMessage = (msg: ConversationMessage): Message => {
    const content =
      msg.response_type === 'plan_applied' || msg.response_type === 'plan_not_applied'
        ? applyConfirmReply(msg.content, msg.response_type)
        : msg.content;
    return {
      id: msg.id,
      type: msg.message_type === 'final' ? 'final' : msg.message_type === 'progress' ? 'progress' : 'assistant',
      role: msg.role === 'user' ? 'athlete' : 'coach',
      content,
      timestamp: new Date(msg.created_at),
      stage: msg.stage,
      progress_stage: msg.stage, // Keep for backward compatibility
      transient: msg.transient === true,
      show_plan: msg.show_plan === true,
      plan_items: msg.plan_items,
      message_type: msg.message_type,
      response_type: msg.response_type,
      metadata: msg.metadata,
    };
  };

  /**
   * Replaces transient messages by stage - same stage overwrites previous message
   */
  const replaceTransientByStage = (newMessage: Message, currentMessages: Message[]): Message[] => {
    if (!newMessage.transient || !newMessage.stage) {
      // Non-transient or no stage - just append
      return [...currentMessages, newMessage];
    }

    // Find and replace message with same stage
    const updated = [...currentMessages];
    const existingIndex = updated.findIndex(
      msg => msg.transient === true && msg.stage === newMessage.stage
    );

    if (existingIndex >= 0) {
      updated[existingIndex] = newMessage;
    } else {
      updated.push(newMessage);
    }

    return updated;
  };

  // Poll for conversation messages ONLY when user triggered an action (sendMessage/confirmPlan).
  // The ref is set to true by sendMessage/handleConfirmPlan and reset when polling stops.
  // This guarantees zero polling on idle page load — no more server log spam.
  const userTriggeredPollRef = useRef(false);

  useEffect(() => {
    if (!conversationId) return;
    if (!userTriggeredPollRef.current) return;
    const shouldPoll = mode === 'executing' || mode === 'planning';
    if (!shouldPoll) {
      userTriggeredPollRef.current = false;
      return;
    }

    let pollCount = 0;
    const maxPolls = 60; // 2 min at 2s
    let consecutiveEmpty = 0;
    let stopped = false;

    const intervalId = setInterval(async () => {
      if (stopped) return;
      pollCount++;

      if (pollCount > maxPolls) {
        clearInterval(intervalId);
        stopped = true;
        userTriggeredPollRef.current = false;
        setMode('done');
        return;
      }

      try {
        const backendMessages = await fetchConversationMessages(conversationId);

        // Convert and merge messages
        setMessages(prev => {
          // Check if we already have a final message — if so, skip update
          if (prev.some(m => m.message_type === 'final')) return prev;

          // Process all backend messages
          let updated = [...prev];

          for (const backendMsg of backendMessages) {
            const frontendMsg = convertBackendMessage(backendMsg);

            // Check if we already have this exact message by ID
            const existingIndexById = updated.findIndex(m => m.id === frontendMsg.id);

            if (existingIndexById >= 0) {
              // Update existing message (backend may have updated it)
              updated[existingIndexById] = frontendMsg;
            } else if (frontendMsg.transient && frontendMsg.stage) {
              // Transient message with stage - replace by stage
              updated = replaceTransientByStage(frontendMsg, updated);
            } else if (frontendMsg.content !== EXECUTING_PLACEHOLDER_REPLY) {
              // New non-transient message - append (skip placeholder; only show Apply to calendar when plan is ready)
              updated.push(frontendMsg);
            }
          }
          // When we have a final message, remove the placeholder so only Apply to calendar is shown
          const hasFinal = backendMessages.some(m => m.message_type === 'final');
          const toReturn = hasFinal
            ? updated.filter(m => m.content !== EXECUTING_PLACEHOLDER_REPLY)
            : updated;
          return toReturn;
        });

        if (backendMessages.some(msg => msg.response_type === 'plan_applied')) {
          planAppliedRef.current = true;
        }

        // If we got a final message, stop polling and leave executing/planning so the card shows
        const finalMsg = backendMessages.find(msg => msg.message_type === 'final');
        if (finalMsg) {
          clearInterval(intervalId);
          stopped = true;
          userTriggeredPollRef.current = false;
          setMode('done');
        }

        // If backend returned zero messages, fast-track to stop
        if (backendMessages.length === 0) {
          consecutiveEmpty++;
          if (consecutiveEmpty >= 5) {
            clearInterval(intervalId);
            stopped = true;
            userTriggeredPollRef.current = false;
            setMode('done');
            return;
          }
        } else {
          consecutiveEmpty = 0;
        }
      } catch (error: unknown) {
        log.error('COACH', 'Failed to poll for messages:', error);

        // Use error classification to determine handling
        const classified = classifyApiError(error);

        // TERMINAL or USER_ACTION errors → stop polling immediately
        if (classified.class === 'TERMINAL' || classified.class === 'USER_ACTION') {
          clearInterval(intervalId);
          stopped = true;
          userTriggeredPollRef.current = false;
          return;
        }
      }
    }, 2000); // Poll every 2 seconds

    return () => {
      clearInterval(intervalId);
      stopped = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, mode]);

  const sendMessage = async (overrideMessage?: string) => {
    // F02: Prevent duplicate sends with send lock
    if (isSendingRef.current) return;
    const messageText = overrideMessage ?? input.trim();
    if (!messageText) return;

    // TODO 6: Reset state when starting new conversation from done mode
    if (mode === 'done') {
      setConversationId(null);
      finalPlanRef.current = null;
    }
    const messageId = crypto.randomUUID();

    // F06: Clear input immediately after send starts (only when using input)
    if (!overrideMessage) setInput('');
    
    // Clear draft context when message is sent
    if (draftContext) {
      setDraftContext(null);
      setHasEditedDraft(false);
    }
    
    // F02: Set send lock immediately
    isSendingRef.current = true;
    setIsTyping(true);

    // F08: Log once per chat turn
    log.debug("COACH", "Sending coach message", {
      length: messageText.length,
      messageId
    });

    const userMessage: Message = {
      id: messageId,
      type: 'user',
      role: 'athlete',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    
    // Detect plan intent from last user message
    // Only allow transitions: idle -> executing, or done -> executing (new conversation)
    if (hasPlanIntent(messageText)) {
      if (mode === 'idle' || mode === 'done') {
        // Go straight to executing — the backend starts plan generation immediately.
        // No intermediate "Generate Weekly Plan" button needed.
        resetProgress();
        setProgressStages([]);
        setProposedSteps([]);
        userTriggeredPollRef.current = true;
        setMode('executing');
      }
      // If already in planning/executing, stay in current state
    } else {
      // Non-plan message - reset to idle only if we're not actively executing
      if (mode !== 'executing') {
        setMode('idle');
      }
    }

    try {
      const response = await sendCoachChat(messageText, { message_id: messageId });
      
      // F05: Only process successful responses (200 OK)
      // No retry/resend logic should trigger on success
      
      // Track conversation ID from response if provided
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }
      if (response.proposed_steps && response.proposed_steps.length > 0) {
        setProposedSteps(response.proposed_steps);
      }
      // Navigate to WorkoutDetails if planner returned a workout_id
      if (response.workout_id) {
        navigate(`/workout/${response.workout_id}`);
        return; // Exit early to prevent further message processing
      }
      
      // Handle progress messages (transient)
      if (response.message_type === 'progress') {
        // If backend sends progress_stages array, use it
        if (response.progress_stages && response.progress_stages.length > 0) {
          setProgressStages(response.progress_stages);
        }
        
        // Handle transient progress message with stage
        if (response.progress_stage) {
          const progressMessage: Message = {
            id: (Date.now() + 1).toString(),
            type: 'progress',
            role: 'coach',
            content: response.reply || response.progress_stage,
            timestamp: new Date(),
            progress_stage: response.progress_stage,
            stage: response.progress_stage, // Use stage for replace-by-stage logic
            transient: true,
            message_type: 'progress',
          };
          
          // Use replace-by-stage logic for consistency
          setMessages(prev => replaceTransientByStage(progressMessage, prev));
        }
      } else if (response.message_type === 'final') {
        const reply = response.reply || t('coach.coachChat.heresPlan');
        const displayContent =
          response.response_type === 'plan_applied' || response.response_type === 'plan_not_applied'
            ? applyConfirmReply(response.reply, response.response_type)
            : reply;
        const appliedToCalendar =
          response.response_type === 'plan_applied' ||
          (typeof reply === 'string' && (reply.includes('applied') || reply.includes('saved to your calendar')));
        if (appliedToCalendar) planAppliedRef.current = true;
        // FE3: Collapse progress on final message - remove all transient progress messages
        setMessages(prev => {
          const filtered = prev.filter(msg => !(msg.type === 'progress' && msg.transient));
          const finalMessage: Message = {
            id: (Date.now() + 1).toString(),
            type: 'final',
            role: 'coach',
            content: displayContent,
            timestamp: new Date(),
            show_plan: response.show_plan === true,
            plan_items: response.show_plan === true && response.plan_items && response.plan_items.length > 0 ? response.plan_items : undefined,
            response_type: response.response_type,
            message_type: 'final',
            coachResponse: hasLoadablePlan(response) ? response : undefined,
          };
          // Clear progress stages when final message arrives
          setProgressStages([]);
          return [...filtered, finalMessage];
        });
      } else if (response.reply !== EXECUTING_PLACEHOLDER_REPLY) {
        // Regular assistant message (e.g. confirm success: "Your training plan has been applied...")
        // Skip the execution placeholder — it carries no information the user needs to see;
        // the progress panel already visualises execution state.
        const reply = response.reply || t('coach.coachChat.defaultReply');
        const displayContent =
          response.response_type === 'plan_applied' || response.response_type === 'plan_not_applied'
            ? applyConfirmReply(response.reply, response.response_type)
            : reply;
        const appliedToCalendar =
          response.response_type === 'plan_applied' ||
          (typeof reply === 'string' && (reply.includes('applied') || reply.includes('saved to your calendar')));
        if (appliedToCalendar) planAppliedRef.current = true;
        const coachMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          role: 'coach',
          content: displayContent,
          timestamp: new Date(),
          show_plan: response.show_plan === true,
          plan_items: response.show_plan === true && response.plan_items && response.plan_items.length > 0 ? response.plan_items : undefined,
          response_type: response.response_type,
          message_type: response.message_type,
          coachResponse: hasLoadablePlan(response) ? response : undefined,
        };
        setMessages(prev => [...prev, coachMessage]);
      }
      
      // If the backend returned a clarification (question), reset to idle so the
      // progress panel disappears and the user can answer the question.
      if (response.response_type === 'question' && mode === 'executing') {
        setMode('idle');
        setProgressStages([]);
      }

      // FE-2: Don't transition to done here - wait for progress completion callback
      // The progress panel will call handleProgressComplete when all steps are done
      // This ensures users see the full progress before the chat concludes
    } catch (error) {
      const apiError = error as { message?: string; status?: number };
      const errorContent = apiError.message || t('coach.coachChat.errorGeneric');
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        role: 'coach',
        content: errorContent,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
      // Reset to idle on error
      setMode('idle');
    } finally {
      setIsTyping(false);
      // F02: Release send lock
      isSendingRef.current = false;
    }
  };

  const handleConfirmPlan = async () => {
    // F02: Prevent duplicate sends with send lock
    if (isSendingRef.current) return;
    
    // Only allow transition from planning to executing
    if (mode !== 'planning') {
      return;
    }

    const messageText = 'Yes, generate the weekly plan';
    const messageId = crypto.randomUUID();

    // F02: Set send lock immediately
    isSendingRef.current = true;
    
    // Add confirm message first
    const confirmMessage: Message = {
      id: messageId,
      type: 'user',
      role: 'athlete',
      content: messageText,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, confirmMessage]);

    // Send confirmation message to backend to trigger plan generation
    userTriggeredPollRef.current = true;
    setMode('executing');
    setIsTyping(true);

    // F08: Log once per chat turn
    log.debug("COACH", "Sending coach message", {
      length: messageText.length,
      messageId
    });

    try {
      // F04: Send raw message only - backend extracts slots
      // F07: Include message_id for idempotency
      const response = await sendCoachChat(messageText, { message_id: messageId });
      
      // F05: Only process successful responses (200 OK)
      // No retry/resend logic should trigger on success
      
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }
      if (response.proposed_steps && response.proposed_steps.length > 0) {
        setProposedSteps(response.proposed_steps);
      }
      // Handle progress messages (transient)
      if (response.message_type === 'progress') {
        // If backend sends progress_stages array, use it
        if (response.progress_stages && response.progress_stages.length > 0) {
          setProgressStages(response.progress_stages);
        }
        
        // Handle transient progress message with stage
        if (response.progress_stage) {
          const progressMessage: Message = {
            id: (Date.now() + 1).toString(),
            type: 'progress',
            role: 'coach',
            content: response.reply || response.progress_stage,
            timestamp: new Date(),
            progress_stage: response.progress_stage,
            stage: response.progress_stage, // Use stage for replace-by-stage logic
            transient: true,
            message_type: 'progress',
          };
          
          // Use replace-by-stage logic for consistency
          setMessages(prev => replaceTransientByStage(progressMessage, prev));
        }
      } else if (response.message_type === 'final') {
        const finalContent =
          response.response_type === 'plan_applied' || response.response_type === 'plan_not_applied'
            ? applyConfirmReply(response.reply, response.response_type)
            : (response.reply || t('coach.coachChat.heresPlan'));
        // TODO 2: Store final plan in ref and persist in messages WITHOUT ending execution
        const finalMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: 'final',
          role: 'coach',
          content: finalContent,
          timestamp: new Date(),
          show_plan: response.show_plan === true,
          plan_items: response.show_plan === true && response.plan_items && response.plan_items.length > 0 ? response.plan_items : undefined,
          response_type: response.response_type,
          message_type: 'final',
        };

        // Store in ref for later use in handleProgressComplete
        finalPlanRef.current = finalMessage;

        // FE3: Collapse progress on final message - remove transient progress and placeholder "working on your request" message
        setMessages(prev => {
          const filtered = prev.filter(
            msg =>
              !(msg.type === 'progress' && msg.transient) &&
              msg.content !== EXECUTING_PLACEHOLDER_REPLY
          );
          return [...filtered, finalMessage];
        });
        // FE-2: Don't transition to done here - wait for progress completion callback
        // The progress panel will call handleProgressComplete when all steps are done
      } else if (response.reply !== EXECUTING_PLACEHOLDER_REPLY) {
        // Regular assistant message (skip placeholder - we only show Apply to calendar when plan is ready)
        const regularContent =
          response.response_type === 'plan_applied' || response.response_type === 'plan_not_applied'
            ? applyConfirmReply(response.reply, response.response_type)
            : (response.reply ?? t('coach.coachChat.defaultReply'));
        const coachResponse: Message = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          role: 'coach',
          content: regularContent,
          timestamp: new Date(),
          show_plan: response.show_plan === true,
          plan_items: response.show_plan === true && response.plan_items && response.plan_items.length > 0 ? response.plan_items : undefined,
          response_type: response.response_type,
          message_type: response.message_type,
        };
        setMessages(prev => [...prev, coachResponse]);
        
        // FE-2: Don't transition to done here - wait for progress completion callback
        // The progress panel will call handleProgressComplete when all steps are done
      }
    } catch (error) {
      const apiError = error as { message?: string; status?: number };
      const errorContent = apiError.message || t('coach.coachChat.errorPlan');
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        role: 'coach',
        content: errorContent,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
      // Reset to planning on error so user can try again
      setMode('planning');
    } finally {
      setIsTyping(false);
      // F02: Release send lock
      isSendingRef.current = false;
    }
  };

  const handleProgressComplete = useCallback(() => {
    // TODO 3: Conclude ONLY in handleProgressComplete - single source of truth for completion
    if (mode !== 'executing') return;

    // Ensure final plan exists in the UI
    if (finalPlanRef.current) {
      const alreadyRendered = messages.some(
        m => m.id === finalPlanRef.current!.id
      );

      if (!alreadyRendered) {
        setMessages(prev => [...prev, finalPlanRef.current!]);
      }

      // FE-1: Store first session date for calendar navigation
      if (finalPlanRef.current.plan_items && finalPlanRef.current.plan_items.length > 0) {
        const firstSessionDate = finalPlanRef.current.plan_items
          .map(item => item.date ? new Date(item.date) : null)
          .filter((date): date is Date => date !== null)
          .sort((a, b) => a.getTime() - b.getTime())[0];

        if (firstSessionDate) {
          localStorage.setItem('calendarFocusDate', firstSessionDate.toISOString());
        }
      }
    }

    // P1: Invalidate calendar and show success conclusion only when plan was actually applied (confirm success).
    const wasApplied = planAppliedRef.current;
    if (wasApplied) {
      queryClient.invalidateQueries({ queryKey: ['calendarWeek'] });
      queryClient.invalidateQueries({ queryKey: ['calendarToday'] });
      queryClient.invalidateQueries({ queryKey: ['calendarSeason'] });
      planAppliedRef.current = false;
    }

    if (wasApplied) {
      setMessages(prev => [
        ...prev,
        {
          id: `concluded-${Date.now()}`,
          type: 'assistant',
          role: 'coach',
          content:
            t('coach.coachChat.planComplete'),
          timestamp: new Date(),
        },
      ]);
    }

    // 🔥 END EXECUTION
    setMode('done');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, queryClient, t]);

  const previewAwaitingConfirmation = (() => {
    if (messages.length === 0) return false;
    const last = messages[messages.length - 1];
    return last.role === 'coach' && isPlanPreviewMessage(last);
  })();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (previewAwaitingConfirmation) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (previewAwaitingConfirmation) return;
    sendMessage();
  };

  const handleApplyPlanPreview = () => {
    sendMessage('Apply it');
  };

  const handleCancelPlanPreview = () => {
    sendMessage('Cancel');
  };

  const handleMacro = (message: string) => {
    sendMessage(message);
  };

  return (
    <div className="flex-1 flex flex-col rounded-lg border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm overflow-hidden">
      {/* Garmin data attribution */}
      <div className="px-4 pt-2">
        <p className="text-[9px] text-muted-foreground/70 leading-tight">
          Insights derived in part from Garmin device-sourced data.
        </p>
      </div>
      {/* Draft Context Banner */}
      {draftContext?.context === 'modify_today_session' && (
        <div className="border-b border-yellow-500/30 bg-yellow-500/5 px-4 py-2">
          <p className="text-sm text-yellow-600 dark:text-yellow-400">
            {t('coach.coachChat.draftBanner')}
          </p>
        </div>
      )}
      
      {/* Messages */}
      {/* Contract: plan_week + preview responses must NEVER render as chat. Only WeeklyPlanPreviewCard. */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <PlanningProgressPanel />
        
        {/* FE2: Render progress messages as checklist */}
        {(() => {
          const progressMessages = messages.filter(msg => msg.type === 'progress');
          const hasFinalMessage = messages.some(msg => msg.type === 'final');
          
          // FE3: Hide progress when final message arrives, but still show transient messages
          if (hasFinalMessage) {
            // Filter out old progress messages, but keep transient messages that are still relevant
            const filtered = messages.filter(msg => {
              // Keep all non-progress messages
              if (msg.type !== 'progress') return true;
              // Keep transient messages (they're progress updates)
              if (msg.transient) return true;
              return false;
            });
            const toRender = deduplicatePlanPreviewMessages(filtered);
            return toRender.map((message) => (
                <div key={message.id} className="space-y-2">
                  {message.transient ? (
                    // Transient message - distinct styling: spinner, muted, italic, no avatar
                    <div className="flex gap-3 animate-fade-up">
                      <div className="w-8 h-8 flex items-center justify-center shrink-0">
                        <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                      </div>
                      <div className="max-w-[75%] rounded-lg px-4 py-2.5 bg-muted/30 text-muted-foreground">
                        <p className="text-sm leading-relaxed italic">{message.content}</p>
                      </div>
                    </div>
                  ) : isPlanPreviewMessage(message) ? (
                    <div className="animate-fade-up">
                      <WeeklyPlanPreviewCard
                        previewText={message.content}
                        onApply={handleApplyPlanPreview}
                      />
                    </div>
                  ) : (
                    // Regular message — Tactical AI Command bubbles
                    <>
                      <div
                        className={cn(
                          'flex gap-3 animate-fade-up',
                          message.role === 'athlete' && 'flex-row-reverse'
                        )}
                      >
                        <div
                          className={cn(
                            'w-8 h-8 rounded-full flex items-center justify-center shrink-0 relative',
                            message.role === 'coach'
                              ? 'bg-white/[0.05] border border-[#00FFC8]/30 text-[#00FFC8]'
                              : 'bg-white/[0.05] border border-accent/30 text-accent'
                          )}
                        >
                          {message.role === 'coach' ? (
                            <Brain className="h-4 w-4" />
                          ) : (
                            <User className="h-4 w-4" />
                          )}
                          {message.source === 'voice' && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-card border border-border flex items-center justify-center">
                              <Mic className="h-2 w-2 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div
                          className={cn(
                            'max-w-[75%] rounded-lg px-4 py-2.5 backdrop-blur-sm',
                            message.role === 'coach'
                              ? 'bg-white/[0.04] border border-white/[0.08] border-l-2 border-l-[#00FFC8]/60 text-foreground'
                              : 'bg-white/[0.06] border border-white/[0.08] border-r-2 border-r-accent/60 text-foreground'
                          )}
                        >
                          {message.isVoiceStreaming ? (
                            <p className="text-sm leading-relaxed">
                              <TypewriterText text={message.content} isStreaming={true} />
                            </p>
                          ) : message.role === 'coach' ? (
                            <CoachMarkdown content={message.content} />
                          ) : (
                            <p className="text-sm leading-relaxed">{message.content}</p>
                          )}
                        </div>
                      </div>
                      {/* Plan List - show only for the last coach message with a plan (dedupe repeated plan blocks) */}
                      {/* Fix 4: Never render plan for question (clarify); no process-step leak */}
                      {message.role === 'coach' &&
                        message.show_plan === true &&
                        message.plan_items &&
                        message.response_type !== 'question' &&
                        (!message.response_type ||
                          ['plan', 'weekly_plan', 'season_plan', 'session_plan', 'recommendation', 'summary'].includes(message.response_type)) &&
                        (() => {
                          const coachWithPlan = messages.filter(
                            m => m.role === 'coach' && m.show_plan === true && m.plan_items && m.plan_items.length > 0
                          );
                          const isLastPlanMessage = coachWithPlan.length > 0 && coachWithPlan[coachWithPlan.length - 1].id === message.id;
                          return isLastPlanMessage;
                        })() && (
                          <div className={cn('flex gap-3')}>
                            <div className="w-8 shrink-0" />
                            <div className="max-w-[75%]">
                              <PlanList planItems={message.plan_items} />
                            </div>
                          </div>
                        )}
                      {/* TASK-006: Load into Calendar button for plan messages */}
                      {message.role === 'coach' && message.coachResponse && (
                        <div className={cn('flex gap-3')}>
                          <div className="w-8 shrink-0" />
                          <LoadPlanToCalendarButton response={message.coachResponse} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ));
          }
          
          // Build stages from progress_stages state (if backend sent it) or from progress messages
          let stages: Array<{ id: string; label: string; status: 'completed' | 'in_progress' | 'pending' }> = [];
          
          if (progressStages.length > 0) {
            // Use stages from backend if available
            stages = progressStages;
          } else {
            // Build stages from progress messages
            // Collect unique stages in order, mark last as in_progress, previous as completed
            const stageMap = new Map<string, { label: string; lastIndex: number }>();
            progressMessages.forEach((msg, index) => {
              const stageId = msg.progress_stage || `stage-${index}`;
              const label = msg.content || msg.progress_stage || 'Processing...';
              stageMap.set(stageId, { label, lastIndex: index });
            });
            
            const sortedStages = Array.from(stageMap.entries()).sort((a, b) => a[1].lastIndex - b[1].lastIndex);
            const lastStageIndex = progressMessages.length > 0 ? progressMessages.length - 1 : -1;
            
            sortedStages.forEach(([stageId, { label, lastIndex }]) => {
              if (lastIndex < lastStageIndex) {
                stages.push({ id: stageId, label, status: 'completed' });
              } else if (lastIndex === lastStageIndex) {
                stages.push({ id: stageId, label, status: 'in_progress' });
              } else {
                stages.push({ id: stageId, label, status: 'pending' });
              }
            });
          }
          
          // Render progress checklist and regular messages (including transient)
          // DO NOT filter out transient messages - they must be rendered
          const regularMessages = messages.filter(msg => msg.type !== 'progress' && msg.type !== 'final');
          const regularToRender = deduplicatePlanPreviewMessages(regularMessages);

          return (
            <>
              {stages.length > 0 && <CoachProgressList stages={stages} />}
              {regularToRender.map((message) => (
                <div key={message.id} className="space-y-2">
                  {message.transient ? (
                    // Transient message - distinct styling: spinner, muted, italic, no avatar
                    <div className="flex gap-3 animate-fade-up">
                      <div className="w-8 h-8 flex items-center justify-center shrink-0">
                        <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                      </div>
                      <div className="max-w-[75%] rounded-lg px-4 py-2.5 bg-muted/30 text-muted-foreground">
                        <p className="text-sm leading-relaxed italic">{message.content}</p>
                      </div>
                    </div>
                  ) : isPlanPreviewMessage(message) ? (
                    <div className="animate-fade-up">
                      <WeeklyPlanPreviewCard
                        previewText={message.content}
                        onApply={handleApplyPlanPreview}
                      />
                    </div>
                  ) : (
                    // Regular message — Tactical AI Command bubbles
                    <>
                      <div
                        className={cn(
                          'flex gap-3 animate-fade-up',
                          message.role === 'athlete' && 'flex-row-reverse'
                        )}
                      >
                        <div
                          className={cn(
                            'w-8 h-8 rounded-full flex items-center justify-center shrink-0 relative',
                            message.role === 'coach'
                              ? 'bg-white/[0.05] border border-[#00FFC8]/30 text-[#00FFC8]'
                              : 'bg-white/[0.05] border border-accent/30 text-accent'
                          )}
                        >
                          {message.role === 'coach' ? (
                            <Brain className="h-4 w-4" />
                          ) : (
                            <User className="h-4 w-4" />
                          )}
                          {message.source === 'voice' && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-card border border-border flex items-center justify-center">
                              <Mic className="h-2 w-2 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div
                          className={cn(
                            'max-w-[75%] rounded-lg px-4 py-2.5 backdrop-blur-sm',
                            message.role === 'coach'
                              ? 'bg-white/[0.04] border border-white/[0.08] border-l-2 border-l-[#00FFC8]/60 text-foreground'
                              : 'bg-white/[0.06] border border-white/[0.08] border-r-2 border-r-accent/60 text-foreground'
                          )}
                        >
                          {message.isVoiceStreaming ? (
                            <p className="text-sm leading-relaxed">
                              <TypewriterText text={message.content} isStreaming={true} />
                            </p>
                          ) : message.role === 'coach' ? (
                            <CoachMarkdown content={message.content} />
                          ) : (
                            <p className="text-sm leading-relaxed">{message.content}</p>
                          )}
                        </div>
                      </div>
                      {/* Plan List - show only for the last coach message with a plan (dedupe repeated plan blocks) */}
                      {/* Fix 4: Never render plan for question (clarify); no process-step leak */}
                      {message.role === 'coach' &&
                        message.show_plan === true &&
                        message.plan_items &&
                        message.response_type !== 'question' &&
                        (!message.response_type ||
                          ['plan', 'weekly_plan', 'season_plan', 'session_plan', 'recommendation', 'summary'].includes(message.response_type)) &&
                        (() => {
                          const coachWithPlan = messages.filter(
                            m => m.role === 'coach' && m.show_plan === true && m.plan_items && m.plan_items.length > 0
                          );
                          const isLastPlanMessage = coachWithPlan.length > 0 && coachWithPlan[coachWithPlan.length - 1].id === message.id;
                          return isLastPlanMessage;
                        })() && (
                          <div className={cn('flex gap-3')}>
                            <div className="w-8 shrink-0" />
                            <div className="max-w-[75%]">
                              <PlanList planItems={message.plan_items} />
                            </div>
                          </div>
                        )}
                      {/* TASK-006: Load into Calendar button for plan messages */}
                      {message.role === 'coach' && message.coachResponse && (
                        <div className={cn('flex gap-3')}>
                          <div className="w-8 shrink-0" />
                          <LoadPlanToCalendarButton response={message.coachResponse} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </>
          );
        })()}

        {/* Runner Processing Indicator - shown when executing, above progress panel */}
        {/* TODO 4: Runner strictly execution-bound - no isTyping check */}
        {mode === 'executing' && (
          <RunnerProcessingIndicator 
            speedMultiplier={progressStages.length > 2 ? 1.5 : 1}
          />
        )}

        {/* Coach Progress Panel - live checklist during plan execution */}
        {(conversationId != null && mode === 'executing') ? (
          <CoachProgressPanel
            conversationId={conversationId}
            mode={mode}
            onComplete={handleProgressComplete}
          />
        ) : null}

        {isTyping && <SystemProcessingIndicator />}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {/* Tactical Macros */}
      <TacticalMacros onMacro={handleMacro} disabled={isTyping || isSendingRef.current || previewAwaitingConfirmation} />

      <div className="border-t border-white/[0.06] p-4">
        {previewAwaitingConfirmation ? (
          <>
            <p className="text-xs text-muted-foreground mb-2">
              {t('coach.coachChat.applyHint')}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelPlanPreview}
              disabled={isTyping || isSendingRef.current}
              className="w-full"
            >
              {t('coach.coachChat.cancel')}
            </Button>
          </>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Textarea
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (draftContext && !hasEditedDraft && e.target.value !== draftContext.draft_message) {
                    setHasEditedDraft(true);
                  }
                }}
                onKeyDown={handleKeyDown}
                placeholder={t('coach.placeholder')}
                className={cn("min-h-[44px] resize-none", draftContext ? "max-h-48" : "max-h-32")}
                rows={draftContext ? 4 : 1}
              />
              <Button
                type="submit"
                disabled={!input.trim() || isTyping || isSendingRef.current}
                size="icon"
                className="shrink-0 bg-coach hover:bg-coach/90 text-coach-foreground"
              >
                <Send className="h-4 w-4" />
              </Button>
              <VoiceButton
                disabled={isTyping || isSendingRef.current || voiceOpen}
                onClick={() => setVoiceOpen(true)}
              />
            </form>
            {voiceOpen && (
              <VoiceOverlay
                onClose={handleVoiceClose}
                onTranscriptUpdate={handleTranscriptUpdate}
              />
            )}
            {draftContext && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                {t('coach.coachChat.draftHint')}
              </p>
            )}
            {!draftContext && (
              <p className="text-xs text-muted-foreground mt-2">
                {t('coach.coachChat.enterHint')}
              </p>
            )}
          </>
        )}
      </div>

    </div>
  );
}
